import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, LayoutGrid } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  CARD_ENTRANCES,
  CARD_IMAGE_FITS,
  CARD_LAYOUTS,
  CARD_TEXT_ANIMS,
  applyCarouselEditPrompt,
  clampCarouselParams,
  clipsInCarouselGroup,
} from '@/lib/cardCarousel';

import { TwinneeLogo } from '@/components/TwinneeLogo';
function FieldLabel({ children }) {
  return <span className="text-[10px] uppercase tracking-wide text-slate-500">{children}</span>;
}

function fieldClass() {
  return 'h-8 bg-black/40 border-[#2a3446] text-xs text-white';
}

export function CardCarouselInspector({
  clip,
  tracks,
  saving = false,
  onParamsChange,
  onClipChange,
  onAiCreate,
  onAiEdit,
}) {
  const groupId = clip?.carouselGroupId || '';
  const members = useMemo(() => clipsInCarouselGroup(tracks, groupId), [tracks, groupId]);
  const params = clampCarouselParams(clip?.carouselParams);
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [aiText, setAiText] = useState('');

  if (!clip) return null;

  const layoutLabel = CARD_LAYOUTS.find((row) => row.id === params.layout)?.label || 'Cards';
  const cardCount = members.length || 1;
  const title = String(clip.cardTitle || clip.name || 'Card').trim();

  const runAi = () => {
    const text = aiText.trim();
    if (!text) return;
    if (/create|show|build|add/i.test(text) && onAiCreate) onAiCreate(text);
    else if (onAiEdit) onAiEdit(applyCarouselEditPrompt(params, text), text);
    setAiText('');
  };

  return (
    <div
      className="rounded-lg border border-[#1f2635] bg-[#12151d]"
      data-testid="card-carousel-inspector"
    >
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
        onClick={() => setOpen((prev) => !prev)}
        data-testid="card-carousel-inspector-toggle"
        aria-expanded={open}
      >
        <LayoutGrid className="w-3.5 h-3.5 text-yellow-300 shrink-0" />
        <span className="text-xs font-semibold text-white truncate">Cards</span>
        <span className="text-[11px] text-slate-500 truncate">
          {cardCount} · {layoutLabel}
          {title ? ` · ${clip.cardNumber || ''}${clip.cardNumber ? '. ' : ''}${title}` : ''}
        </span>
        {open ? (
          <ChevronUp className="w-3.5 h-3.5 text-slate-500 ml-auto shrink-0" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-slate-500 ml-auto shrink-0" />
        )}
      </button>

      {open ? (
        <div className="px-3 pb-3 space-y-2 border-t border-[#1f2635] pt-2">
          <div className="flex gap-2">
            <label className="w-16 shrink-0 space-y-1">
              <FieldLabel>#</FieldLabel>
              <Input
                value={clip.cardNumber || ''}
                className={fieldClass()}
                onChange={(e) => onClipChange?.({ cardNumber: e.target.value })}
              />
            </label>
            <label className="flex-1 min-w-0 space-y-1">
              <FieldLabel>Title</FieldLabel>
              <Input
                value={clip.cardTitle || ''}
                className={fieldClass()}
                onChange={(e) => onClipChange?.({
                  cardTitle: e.target.value,
                  name: `${clip.cardNumber || ''}. ${e.target.value}`.replace(/^\.\s*/, ''),
                })}
              />
            </label>
            <label className="w-36 shrink-0 space-y-1">
              <FieldLabel>Layout</FieldLabel>
              <Select value={params.layout} onValueChange={(layout) => onParamsChange?.({ layout })}>
                <SelectTrigger className={fieldClass()}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[220]">
                  {CARD_LAYOUTS.map((row) => (
                    <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
          </div>

          <div className="flex gap-2">
            <Input
              value={aiText}
              placeholder="Ask AI to create or restyle these cards"
              className={fieldClass()}
              onChange={(e) => setAiText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runAi();
              }}
              data-testid="card-carousel-ai-input"
            />
            <button
              type="button"
              disabled={saving || !aiText.trim()}
              className="h-8 px-2.5 rounded-md bg-yellow-300/90 text-black text-[11px] font-semibold shrink-0 disabled:opacity-40 inline-flex items-center gap-1"
              onClick={runAi}
              data-testid="card-carousel-ai-run"
            >
              <TwinneeLogo className="w-3 h-3" />
              Run
            </button>
          </div>

          <button
            type="button"
            className="text-[11px] text-slate-500 hover:text-slate-300 inline-flex items-center gap-1"
            onClick={() => setMore((prev) => !prev)}
            data-testid="card-carousel-inspector-more"
          >
            {more ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            {more ? 'Less' : 'Motion & style'}
          </button>

          {more ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <label className="space-y-1">
                <FieldLabel>Entrance</FieldLabel>
                <Select value={params.animationIn} onValueChange={(animationIn) => onParamsChange?.({ animationIn })}>
                  <SelectTrigger className={fieldClass()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[220]">
                    {CARD_ENTRANCES.map((row) => (
                      <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <FieldLabel>Highlight</FieldLabel>
                <Input
                  value={clip.cardHighlight || ''}
                  placeholder="LIPSTICK"
                  className={fieldClass()}
                  onChange={(e) => onClipChange?.({ cardHighlight: e.target.value })}
                />
              </label>
              <label className="space-y-1">
                <FieldLabel>Title motion</FieldLabel>
                <Select value={params.titleAnim} onValueChange={(titleAnim) => onParamsChange?.({ titleAnim })}>
                  <SelectTrigger className={fieldClass()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[220]">
                    {CARD_TEXT_ANIMS.map((row) => (
                      <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <FieldLabel>Stagger (s)</FieldLabel>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  max="1.2"
                  value={params.staggerDelay}
                  className={fieldClass()}
                  onChange={(e) => onParamsChange?.({ staggerDelay: Number(e.target.value) })}
                />
              </label>
              <label className="space-y-1">
                <FieldLabel>Motion (s)</FieldLabel>
                <Input
                  type="number"
                  step="0.05"
                  min="0.15"
                  max="1.2"
                  value={params.animationDuration}
                  className={fieldClass()}
                  onChange={(e) => onParamsChange?.({ animationDuration: Number(e.target.value) })}
                />
              </label>
              <label className="space-y-1">
                <FieldLabel>Size</FieldLabel>
                <Input
                  type="number"
                  step="0.05"
                  min="0.55"
                  max="1.6"
                  value={params.scale}
                  className={fieldClass()}
                  onChange={(e) => onParamsChange?.({ scale: Number(e.target.value) })}
                />
              </label>
              <label className="space-y-1">
                <FieldLabel>Image fit</FieldLabel>
                <Select value={params.imageFit} onValueChange={(imageFit) => onParamsChange?.({ imageFit })}>
                  <SelectTrigger className={fieldClass()}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[220]">
                    {CARD_IMAGE_FITS.map((row) => (
                      <SelectItem key={row.id} value={row.id}>{row.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <FieldLabel>Radius</FieldLabel>
                <Input
                  type="number"
                  step="0.02"
                  min="0"
                  max="0.4"
                  value={params.borderRadius}
                  className={fieldClass()}
                  onChange={(e) => onParamsChange?.({ borderRadius: Number(e.target.value) })}
                />
              </label>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
